import React, { useContext, useEffect, useState, useRef } from 'react';
import { Form, Input, Button, Tooltip, Tag } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../../context/auth/AuthContext.js';
import checkAdminAuth from '../../helpers/AdminAuth.js';
import { Link, useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Typography, Checkbox, CssBaseline,
  FormControlLabel, Grid, Paper,
} from '@mui/material';
import logo from '../../assets/lightLogo.png';
import Footer from 'components/Footer.js';
import Cookies from 'js-cookie';


const TEST_CREDENTIALS = [
  { role: 'User',       email: 'oft.user@gmail.com', password: 'password' },
  { role: 'Admin',      email: 'oft.adm@gmail.com',  password: 'password' },
  { role: 'SuperAdmin', email: 'oft.sadm@gmail.com', password: 'password' },
];


const RECENT_KEY = 'oft_recent_emails';

function getRecentEmails() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}

function saveRecentEmail(email) {
  const arr = getRecentEmails().filter((e) => e !== email);
  arr.unshift(email);
  localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, 3)));
}


const backgroundImages = [
  'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1740&auto=format&fit=crop)',
  'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1170&auto=format&fit=crop)',
  'url(https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?q=80&w=1742&auto=format&fit=crop)',
  'url(https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1740&auto=format&fit=crop)',
  'url(https://images.unsplash.com/photo-1616587656977-ac36a5a430bc?q=80&w=1740&auto=format&fit=crop)',
  'url(https://images.unsplash.com/photo-1711097383282-28097ae16b1d?q=80&w=1740&auto=format&fit=crop)',
  'url(https://images.unsplash.com/flagged/photo-1559733404-9b79677fc959?q=80&w=1740&auto=format&fit=crop)',
];


const CredentialsTooltip = ({ onSelect }) => (
  <div style={{ minWidth: 270 }}>
    <p style={{
      margin: '0 0 10px 0', fontWeight: 600, fontSize: 13, color: '#fff',
      borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: 8,
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      <InfoCircleOutlined style={{ fontSize: 14 }} />
      Test Credentials — click Here to login
    </p>

    {TEST_CREDENTIALS.map(({ role, email, password }) => (
      <div
        key={role}
        onClick={() => onSelect(email, password)}
        style={{
          marginBottom: 8, padding: '8px 10px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: 8, cursor: 'pointer',
          border: '1px solid transparent', transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.18)';
          e.currentTarget.style.border = '1px solid rgba(255,255,255,0.25)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
          e.currentTarget.style.border = '1px solid transparent';
        }}
      >
        <p style={{
          margin: '0 0 5px 0', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.7px', textTransform: 'uppercase', color: '#90cdf4',
        }}>
          {role}
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
          <span style={{ color: 'rgba(255,255,255,0.55)' }}>Email</span>
          <span style={{ color: '#fff', fontFamily: 'monospace', fontSize: 11 }}>{email}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <span style={{ color: 'rgba(255,255,255,0.55)' }}>Password</span>
          <span style={{ color: '#fff', fontFamily: 'monospace', fontSize: 11 }}>{password}</span>
        </div>
      </div>
    ))}

    <p style={{ margin: '6px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
      For testing purposes only
    </p>
  </div>
);


const LoginForm = () => {
  const { LoginAction, state, AuthReset } = useContext(AuthContext);
  const { token, loading } = state;

  const [form]                                      = Form.useForm();
  const [rememberMe, setRememberMe]                 = useState(!!Cookies.get('rememberedEmail'));
  const [passwordVisible, setPasswordVisible]       = useState(false);
  const [recentEmails, setRecentEmails]             = useState(getRecentEmails);
  const [selectedBackground, setSelectedBackground] = useState('');
  const [tooltipOpen, setTooltipOpen]               = useState(false);

  // ── Ref to track whether login was triggered by credential auto-select ──
  const isAutoLogin = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    const idx = Math.floor(Math.random() * backgroundImages.length);
    setSelectedBackground(backgroundImages[idx]);
  }, []);

  useEffect(() => { AuthReset(); }, [AuthReset]);

  useEffect(() => {
    if (token) {
      const authed = checkAdminAuth(token);
      if (authed.role === 'admin' || authed.role === 'user') navigate('/dashboard');
      else if (authed.role === 'superadmin') navigate('/createuser');
    }
  }, [token, navigate]);

  const onFinish = (values) => {
    if (rememberMe) {
      Cookies.set('rememberedEmail',    values.email,    { expires: 30 });
      Cookies.set('rememberedPassword', values.password, { expires: 30 });
    } else {
      Cookies.remove('rememberedEmail');
      Cookies.remove('rememberedPassword');
    }

    // ── Only save to recent logins when user typed manually, not auto-select ──
    if (!isAutoLogin.current) {
      saveRecentEmail(values.email);
      setRecentEmails(getRecentEmails());
    }

    isAutoLogin.current = false; // reset ref after every submit
    LoginAction(values);
  };

  // ── Click a credential row → fill fields → submit immediately ──
  const handleCredentialSelect = (email, password) => {
    form.setFieldsValue({ email, password });
    setTooltipOpen(false);
    isAutoLogin.current = true; // mark as auto-login so recent tags are skipped
    setTimeout(() => form.submit(), 150);
  };

  return (
    <Grid>
      <CssBaseline />
      <Grid
        container
        component="main"
        sx={{ minHeight: '92vh', flexDirection: { xs: 'column', sm: 'row' } }}
      >
        {/* ── Left panel ── */}
        <Grid
          item xs={12} sm={4} md={7}
          sx={{
            display: 'flex',
            minHeight: { xs: '220px', sm: 'auto' },
            backgroundImage: `linear-gradient(to bottom, rgb(51 176 255 / 40%), rgba(0,0,0,0.45)), ${selectedBackground}`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{
            width: '100%', height: '100%', textAlign: 'center', color: '#fff',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', px: { xs: 2, sm: 4 }, py: { xs: 3, sm: 6 },
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}>
            <Typography variant="h2" sx={{ fontWeight: 700, fontSize: { xs: 22, sm: 28, md: 36 }, color: '#fff' }}>
              Welcome to OneFinTrace
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 16, md: 20 }, color: '#fff', mt: 0.5 }}>
              AI for Financial Operations, Simplified.
            </Typography>
            <Typography sx={{ mt: 1, opacity: 0.9, fontSize: { xs: 12, sm: 14, md: 16 }, color: '#fff', display: { xs: 'none', sm: 'block' } }}>
              Empower your business with AI-powered financial intelligence, <br />
              automated invoice management, and real-time customer insights.
            </Typography>
            <Box sx={{ mt: { xs: 1, sm: 3 }, fontSize: { xs: 11, sm: 14 }, opacity: 0.9, color: '#fff' }}>
              <Typography>✓ Real-Time Analytics</Typography>
              <Typography>✓ Real-Time Financial Intelligence</Typography>
              <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>✓ Smart Financial Tools</Typography>
              <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>✓ Consolidated Invoice Tracking</Typography>
              <Typography>✓ Unified Customer Insights</Typography>
              <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>✓ Customer Activity & Revenue Tracking</Typography>
            </Box>
          </Box>
        </Grid>

        {/* ── Right panel ── */}
        <Grid
          item xs={12} sm={8} md={5}
          sx={{
            backgroundColor: '#f0f0f0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 4 },
          }}
        >
          <Paper
            elevation={8}
            square={false}
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: 380, md: 400 },
              borderRadius: 2,
              p: { xs: 3, sm: 4 },
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              textAlign: 'center', width: '100%', maxWidth: 300, mx: 'auto',
            }}>

              <Box sx={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', width: '100%', mb: 0.5,
              }}>
                {/* left spacer (same width as i icon box so logo stays centred) */}
                <Box sx={{ width: 32 }} />

                <Avatar
                  alt="OneFinTrace Logo"
                  src={logo}
                  sx={{ width: 180, height: 35 }}
                  variant="square"
                />

                {/* ── i icon — top-right of the card ── */}
                <Tooltip
                  open={tooltipOpen}
                  onOpenChange={setTooltipOpen}
                  title={<CredentialsTooltip onSelect={handleCredentialSelect} />}
                  placement="bottomRight"
                  color="#1a2b40"
                  overlayInnerStyle={{ padding: '12px 14px', borderRadius: 10, minWidth: 290 }}
                  trigger={['click']}
                  arrow={{ pointAtCenter: true }}
                >
                  <Box
                    sx={{
                      width: 32, height: 32,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: '50%', cursor: 'pointer',
                      transition: 'background 0.2s',
                      '&:hover': { background: '#e6f4ff' },
                    }}
                  >
                    <InfoCircleOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                  </Box>
                </Tooltip>
              </Box>

              <p className="text small1" style={{ marginTop: '8px' }}>
                Enter your login credentials to proceed
              </p>

              <Form
                form={form}
                name="normal_login"
                style={{ marginTop: '10px', width: '100%' }}
                className="login-form"
                onFinish={onFinish}
              >
                {/* ── Email ── */}
                <Form.Item
                  name="email"
                  initialValue={Cookies.get('rememberedEmail') || ''}
                  rules={[
                    { required: true, message: 'Please enter your registered email address' },
                    { type: 'email',  message: 'Please enter a valid email address' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Email"
                    maxLength={50}
                    size="large"
                    style={{ backgroundColor: 'white' }}
                  />
                </Form.Item>

                {/* ── Password ── */}
                <Form.Item
                  name="password"
                  initialValue={Cookies.get('rememberedPassword') || ''}
                  rules={[{ required: true, message: 'Please input your password' }]}
                  validateTrigger="onSubmit"
                >
                  <Input
                    prefix={<LockOutlined />}
                    type={passwordVisible ? 'text' : 'password'}
                    placeholder="Password"
                    maxLength={20}
                    size="large"
                    suffix={
                      <span
                        onClick={() => setPasswordVisible((v) => !v)}
                        style={{ cursor: 'pointer', color: '#aaa', fontSize: 16 }}
                      >
                        {passwordVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                      </span>
                    }
                  />
                </Form.Item>

                {/* ── Recent emails — hidden during loading & hidden for auto-login ── */}
                {recentEmails.length > 0 && !loading && (
                  <div style={{ textAlign: 'left', marginBottom: 10 }}>
                    <span style={{ fontSize: 12, color: '#888' }}>Recent logins: </span>
                    {recentEmails.map((email) => (
                      <Tag
                        key={email}
                        style={{ cursor: 'pointer', marginBottom: 4, fontSize: 12 }}
                        onClick={() => form.setFieldsValue({ email })}
                      >
                        {email}
                      </Tag>
                    ))}
                  </div>
                )}

                {/* ── Remember me ── */}
                <FormControlLabel
                  control={
                    <Checkbox
                      name="remember"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="custom-checkbox"
                    />
                  }
                  label={<span style={{ color: 'black' }}>Remember me</span>}
                />

                {/* ── Submit ── */}
                <Box sx={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginTop: 5,
                }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="login-form-button"
                    loading={loading}
                  >
                    Login
                  </Button>
                  <Link to="/user/ForgotPassword" className="btn-log">
                    Forgot password?
                  </Link>
                </Box>
              </Form>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Footer />
    </Grid>
  );
};

export default LoginForm;
